import { useAccount } from "@qw/auth";
import {
  useDeletePlaybookTriggerMutation,
  useMyInvokablePlaybooksQuery,
  useMyPlaybookTriggerQuery,
  useMyPlaybookTriggersQuery,
  useSetPlaybookTriggerMutation,
  useTriggerPolicyExpirationCheckMutation,
} from "@qw/graphql";
import { INTERNAL_ROLES } from "@qw/qw-common";
import { Button, Column, ColumnContent, Icon, Input, Spinner, useToast } from "@qw/ui";
import { useState } from "react";
import { PlaybookPicker } from "./playbook-picker";
import { ScheduleTriggersSection } from "./schedule-triggers-section";

const OPPORTUNITY_ASSIGNED_EVENT_TYPE = "Opportunity.assignedToBroker";
const EMAIL_RECEIVED_EVENT_TYPE = "Email.received";
const POLICY_EXPIRING_EVENT_TYPE = "Policy.expiringInDays";

interface TriggerSectionProps {
  eventType: string;
  title: string;
  description: string;
  icon: string;
  successMessage: string;
  removeMessage: string;
  allPlaybooks: Array<{ id: string; name?: string | null; description?: string | null }>;
  isLoading: boolean;
}

const TriggerSection = ({
  eventType,
  title,
  description,
  icon,
  successMessage,
  removeMessage,
  allPlaybooks,
  isLoading: parentLoading,
}: TriggerSectionProps) => {
  const { toast } = useToast();
  const [localSelectedPlaybookId, setLocalSelectedPlaybookId] = useState<string | undefined>(undefined);

  const {
    data: triggerData,
    loading: triggerLoading,
    refetch: refetchTrigger,
  } = useMyPlaybookTriggerQuery({
    variables: { eventType },
  });

  const currentTrigger = triggerData?.myPlaybookTrigger;

  const [setTrigger, { loading: settingTrigger }] = useSetPlaybookTriggerMutation({
    onCompleted: () => {
      toast({
        title: "Trigger saved",
        description: successMessage,
      });
      setLocalSelectedPlaybookId(undefined);
      refetchTrigger();
    },
    onError: (error) => {
      toast({
        title: "Error saving trigger",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const [deleteTrigger, { loading: deletingTrigger }] = useDeletePlaybookTriggerMutation({
    onCompleted: () => {
      toast({
        title: "Trigger removed",
        description: removeMessage,
      });
      setLocalSelectedPlaybookId(undefined);
      refetchTrigger();
    },
    onError: (error) => {
      toast({
        title: "Error removing trigger",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const selectedPlaybookId = localSelectedPlaybookId ?? currentTrigger?.playbookId;
  const isSaving = settingTrigger || deletingTrigger;
  const hasChanges = selectedPlaybookId !== currentTrigger?.playbookId;
  const isLoading = parentLoading || triggerLoading;

  const handleSave = () => {
    if (!selectedPlaybookId) {
      return;
    }

    setTrigger({
      variables: {
        input: {
          eventType,
          playbookId: selectedPlaybookId,
        },
      },
    });
  };

  const handleRemove = () => {
    deleteTrigger({
      variables: {
        eventType,
      },
    });
  };

  return (
    <div className="rounded-lg border bg-card p-6 mb-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="rounded-full bg-primary/10 p-3">
          <Icon icon={icon as any} className="text-2xl text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold mb-1">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Select Playbook</label>
            <PlaybookPicker playbooks={allPlaybooks} value={selectedPlaybookId} onChange={setLocalSelectedPlaybookId} />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={!selectedPlaybookId || !hasChanges || isSaving} size="sm">
              {isSaving ? <Spinner className="size-4 mr-2" /> : null}
              {currentTrigger ? "Update Trigger" : "Save Trigger"}
            </Button>
            {currentTrigger && (
              <Button onClick={handleRemove} variant="outline" disabled={isSaving} size="sm">
                Remove Trigger
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface PolicyExpiringTriggersSectionProps {
  allPlaybooks: Array<{ id: string; name?: string | null; description?: string | null }>;
  isLoading: boolean;
}

const PolicyExpiringTriggersSection = ({
  allPlaybooks,
  isLoading: parentLoading,
}: PolicyExpiringTriggersSectionProps) => {
  const { toast } = useToast();
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string | undefined>(undefined);
  const [daysInput, setDaysInput] = useState<string>("");

  const { data: triggersData, loading: triggersLoading, refetch: refetchTriggers } = useMyPlaybookTriggersQuery();

  const { user } = useAccount();
  const hasInternalRole = user.roles.some((role) => INTERNAL_ROLES.includes(role as any));

  const [triggerExpirationCheck, { loading: triggeringCheck }] = useTriggerPolicyExpirationCheckMutation({
    onCompleted: () => {
      toast({
        title: "Triggers executed",
        description: "Policy expiration triggers have been executed. Any matching playbooks have been run.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error running triggers",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Filter to just Policy.expiringInDays triggers
  const policyExpiringTriggers =
    triggersData?.myPlaybookTriggers?.filter((t) => t.eventType === POLICY_EXPIRING_EVENT_TYPE) ?? [];

  const [setTrigger, { loading: settingTrigger }] = useSetPlaybookTriggerMutation({
    onCompleted: () => {
      toast({
        title: "Trigger saved",
        description: "Your policy expiration trigger has been configured.",
      });
      setSelectedPlaybookId(undefined);
      setDaysInput("");
      refetchTriggers();
    },
    onError: (error) => {
      toast({
        title: "Error saving trigger",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const [deleteTrigger, { loading: deletingTrigger }] = useDeletePlaybookTriggerMutation({
    onCompleted: () => {
      toast({
        title: "Trigger removed",
        description: "Your policy expiration trigger has been removed.",
      });
      refetchTriggers();
    },
    onError: (error) => {
      toast({
        title: "Error removing trigger",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isLoading = parentLoading || triggersLoading;
  const isSaving = settingTrigger || deletingTrigger;

  const daysValue = daysInput ? Number(daysInput) : undefined;
  const canSave = selectedPlaybookId && daysValue && daysValue > 0 && daysValue <= 365;

  const handleSave = () => {
    if (!selectedPlaybookId || !daysValue) {
      return;
    }

    setTrigger({
      variables: {
        input: {
          eventType: POLICY_EXPIRING_EVENT_TYPE,
          playbookId: selectedPlaybookId,
          triggerConfig: { daysUntil: daysValue },
        },
      },
    });
  };

  const handleRemove = (daysUntil: number) => {
    deleteTrigger({
      variables: {
        eventType: POLICY_EXPIRING_EVENT_TYPE,
        triggerConfig: { daysUntil },
      },
    });
  };

  return (
    <div className="rounded-lg border bg-card p-6 mb-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="rounded-full bg-primary/10 p-3">
          <Icon icon="event_upcoming" className="text-2xl text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold mb-1">Policy Expiring Soon</h2>
            {hasInternalRole && (
              <Button variant="outline" size="sm" onClick={() => triggerExpirationCheck()} disabled={triggeringCheck}>
                {triggeringCheck ? <Spinner className="size-4 mr-2" /> : null}
                Run Triggers Now
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Automatically run a playbook a certain number of days before a policy expires. You can configure multiple
            triggers for different time windows.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Existing triggers */}
          {policyExpiringTriggers.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium block">Active Triggers</label>
              <div className="space-y-2">
                {policyExpiringTriggers.map((trigger) => {
                  const daysUntil = (trigger.triggerConfig as { daysUntil?: number })?.daysUntil;
                  return (
                    <div key={trigger.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{daysUntil} days</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="text-sm">{trigger.playbook?.name || "(untitled playbook)"}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => daysUntil !== null && daysUntil !== undefined && handleRemove(daysUntil)}
                        disabled={isSaving}
                      >
                        <Icon icon="close" className="text-lg" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add new trigger */}
          <div className="space-y-3 pt-2 border-t">
            <label className="text-sm font-medium block">Add New Trigger</label>

            {/* Days input */}
            <div>
              <label className="text-xs text-muted-foreground mb-2 block">Days before expiration</label>
              <Input
                type="number"
                placeholder="Enter number of days"
                value={daysInput}
                onChange={(e) => setDaysInput(e.target.value)}
                className="w-48"
                min={1}
                max={365}
              />
            </div>

            {/* Playbook selection */}
            <div>
              <label className="text-xs text-muted-foreground mb-2 block">Playbook to run</label>
              <PlaybookPicker playbooks={allPlaybooks} value={selectedPlaybookId} onChange={setSelectedPlaybookId} />
            </div>

            <Button onClick={handleSave} disabled={!canSave || isSaving} size="sm">
              {isSaving ? <Spinner className="size-4 mr-2" /> : null}
              Add Trigger
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const PlaybooksTriggers = () => {
  // Triggers auto-fire a playbook at the top level, with no parent
  // orchestrator context — same constraint the LLM's playbookDiscovery tool
  // operates under. myInvokablePlaybooks returns owned + subscribed
  // filtered to type=Workflow server-side, so this picker only ever offers
  // playbooks the trigger fire-path can actually dispatch.
  const { data: invokableData, loading: invokableLoading } = useMyInvokablePlaybooksQuery();
  const allPlaybooks = invokableData?.myInvokablePlaybooks ?? [];
  const isLoading = invokableLoading;

  if (isLoading) {
    return (
      <div className="flex-auto flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Column className="flex-auto bg-background">
      <ColumnContent className="p-4 max-w-2xl">
        {/* Default Intake Playbook Trigger */}
        <TriggerSection
          eventType={OPPORTUNITY_ASSIGNED_EVENT_TYPE}
          title="Default Intake Playbook"
          description="Automatically run a playbook when you are assigned a new opportunity. This helps you get started quickly with a consistent intake process."
          icon="assignment_ind"
          successMessage="Your default intake playbook has been configured."
          removeMessage="Your default intake playbook has been removed."
          allPlaybooks={allPlaybooks}
          isLoading={isLoading}
        />

        {/* Any Email Received Trigger */}
        <TriggerSection
          eventType={EMAIL_RECEIVED_EVENT_TYPE}
          title="Any Email Received"
          description="Automatically run a playbook whenever you receive an email. The playbook will run at the conversation level, allowing you to process and respond to emails automatically."
          icon="mail"
          successMessage="Your email received playbook has been configured."
          removeMessage="Your email received playbook has been removed."
          allPlaybooks={allPlaybooks}
          isLoading={isLoading}
        />

        {/* Policy Expiring Soon Trigger */}
        <PolicyExpiringTriggersSection allPlaybooks={allPlaybooks} isLoading={isLoading} />

        {/* Weekday Schedule Trigger */}
        <ScheduleTriggersSection allPlaybooks={allPlaybooks} isLoading={isLoading} />
      </ColumnContent>
    </Column>
  );
};
